import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Table, Icon } from 'semantic-ui-react';
import { Loader, Link, Flex } from 'nallian-shared-ui/lib/components';
import HasRight from 'nallian-shared-ui/lib/layout/rights/HasRight';
import { TABS, FEATURE_TABS, APP_INSTALLATION_RESOURCE } from '../../constants';
import RemoveFeatureService from '../../services/removeFeatureService';
import FeatureRoles from '../feature/FeatureRoles';
import TypeToConfirmModal from '../../../app/components/TypeToConfirmModal';
import { CursorPointer } from '../../../../portal/app/styles';

const RedSpan = styled.span`
  color: red;
`;

const AppInstallationFeatureOverview = ({
  appInstallationFetch,
  appId,
  installationId,
  dispatchRemoveFeatureDelete,
  removeFeatureFetch,
}) => {
  const { t } = useTranslation();
  const [featureToDelete, setFeatureToDelete] = useState(null);

  const handleSetFeatureForDeletion = useCallback(
    featureItem => setFeatureToDelete(featureItem),
    []
  );

  const handleCancelFeatureDeletion = useCallback(() => setFeatureToDelete(null), []);

  const handleConfirmFeatureDeletion = useCallback(() => {
    if (featureToDelete && dispatchRemoveFeatureDelete) {
      dispatchRemoveFeatureDelete(
        appId,
        installationId,
        featureToDelete.id,
        () => setFeatureToDelete(null),
        appInstallationFetch.value,
        APP_INSTALLATION_RESOURCE,
        t
      );
    }
  }, [
    featureToDelete,
    dispatchRemoveFeatureDelete,
    appId,
    installationId,
    appInstallationFetch.value,
    t,
  ]);

  return (
    <>
      <Table basic="very">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width="3">{t('featureName')}</Table.HeaderCell>
            <Table.HeaderCell width="2">{t('isGlobal')}</Table.HeaderCell>
            <Table.HeaderCell width="2">{t('isCrossCommunity')}</Table.HeaderCell>
            <Table.HeaderCell width="2">{t('requiresSubscription')}</Table.HeaderCell>
            <Table.HeaderCell width="4">{t('roles')}</Table.HeaderCell>
            <Table.HeaderCell width="1" />
            <Table.HeaderCell width="1">
              <HasRight right="apps/installations/features/delete">{t('actions')}</HasRight>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {(appInstallationFetch.value?.appInstallationFeatures || []).map(f => (
            <Table.Row key={`feature_${f.id}`}>
              <Table.Cell width="3">
                <Link
                  to={`/apps/${appId}/${TABS.INSTALLATIONS}/${installationId}/features/${f.id}/${FEATURE_TABS.DETAILS}`}
                >
                  {f.name}
                </Link>
              </Table.Cell>
              <Table.Cell width="2">
                <Icon
                  name={f.isGlobal ? 'checkmark' : 'minus'}
                  color={f.isGlobal ? 'green' : 'gray'}
                />
              </Table.Cell>
              <Table.Cell width="2">
                <Icon
                  name={f.isCrossCommunity ? 'checkmark' : 'minus'}
                  color={f.isCrossCommunity ? 'green' : 'gray'}
                />
              </Table.Cell>
              <Table.Cell width="2">
                <Icon
                  name={f.requiresSubscription ? 'checkmark' : 'minus'}
                  color={f.requiresSubscription ? 'green' : 'gray'}
                />
              </Table.Cell>
              <Table.Cell width="4">
                <FeatureRoles
                  rootUrl={`/apps/${appId}/${TABS.INSTALLATIONS}/${installationId}`}
                  value={f.appInstallationFeatureRoles}
                />
              </Table.Cell>
              <Table.Cell width="2">
                {f.isObsolete && (
                  <Flex>
                    <Icon name="exclamation triangle" color="red" />
                    <RedSpan>{t('isObsolete')}</RedSpan>
                  </Flex>
                )}
              </Table.Cell>
              <Table.Cell width="1">
                <HasRight right="apps/installations/features/delete">
                  <CursorPointer>
                    {removeFeatureFetch?.meta?.featureId === f.id && removeFeatureFetch?.pending ? (
                      <Loader inline active size="mini" />
                    ) : (
                      <Icon
                        iconbutton
                        name="trash"
                        onClick={() => handleSetFeatureForDeletion(f)}
                      />
                    )}
                  </CursorPointer>
                </HasRight>
              </Table.Cell>
            </Table.Row>
          ))}
          {appInstallationFetch.pending && (
            <Flex fluid justifyContent="center">
              <Loader inline active />
            </Flex>
          )}
          {!appInstallationFetch.pending &&
            !appInstallationFetch.value?.appInstallationFeatures?.length && (
              <Table.Row>
                <Table.Cell>{t('noFeaturesYet')}</Table.Cell>
              </Table.Row>
            )}
        </Table.Body>
      </Table>
      {featureToDelete && (
        <TypeToConfirmModal
          header={t('areYouSureDeleteFeature', { name: featureToDelete.name })}
          value={featureToDelete.name}
          extraCheckSentence={
            !featureToDelete.isObsolete ? t('acknowledgeFeatureNotObsolete') : null
          }
          onConfirm={handleConfirmFeatureDeletion}
          onCancel={handleCancelFeatureDeletion}
          loading={removeFeatureFetch?.pending}
        />
      )}
    </>
  );
};

AppInstallationFeatureOverview.propTypes = {
  appInstallationFetch: PropTypes.object.isRequired,
  appId: PropTypes.string.isRequired,
  installationId: PropTypes.string.isRequired,
  dispatchRemoveFeatureDelete: PropTypes.func.isRequired,
  removeFeatureFetch: PropTypes.object.isRequired,
};

AppInstallationFeatureOverview.defaultProps = {};

export default RemoveFeatureService(AppInstallationFeatureOverview);
